import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { map, Observable, of } from 'rxjs';

import { AVAILABLE_MODE_IDS, DYNAMIC_FEATURE_FLAGS, FEATURE_FLAGS_CONFIG, ModeId, staticAvailableModeIds } from 'shared-modules-angular/feature-flags';
import { ILicensingService, LICENSING_SERVICE } from 'shared-modules-angular/licensing';

import { LicensedFeature, UT_DESKTOP_FEATURE_FLAGS_CONFIG, UtDesktopModeFeature } from '../../../core/config/feature-flags.config';
import { ConfigService } from '../../../core/services/config';
import { CryptlexLicenseService } from './cryptlex-license.service';
import { EvCLicenseService } from './evc-license.service';

type LicensedFeatureFlags = Partial<Record<UtDesktopModeFeature, boolean>>;

/**
 * Local dev (`environment.local`) has no license backend to talk to - grant every mode and
 * feature instead of gating on a check that can only ever fail there.
 */
export function provideFeaturesWithLicensing(): EnvironmentProviders {
  return makeEnvironmentProviders([
    /**
     * Features
     */
    { provide: FEATURE_FLAGS_CONFIG, useValue: UT_DESKTOP_FEATURE_FLAGS_CONFIG },
    /**
     * Licensing
     */
    {
      provide: LICENSING_SERVICE,
      useFactory: (config: ConfigService, pcLicense: CryptlexLicenseService, webLicense: EvCLicenseService): ILicensingService<LicensedFeature> => (
        config.isElectron ? pcLicense : webLicense
      ),
      deps: [ConfigService, CryptlexLicenseService, EvCLicenseService],
    },
    /**
     * Available modes
     */
    {
      provide: AVAILABLE_MODE_IDS,
      useFactory: (config: ConfigService, licensing: ILicensingService<LicensedFeature>): Observable<ModeId[]> => (
        config.isLocal
          ? staticAvailableModeIds(UT_DESKTOP_FEATURE_FLAGS_CONFIG)
          : licensing.validLicenses$.pipe(map(validLicenses => (validLicenses.length > 0 ? Object.keys(UT_DESKTOP_FEATURE_FLAGS_CONFIG.modes) : [])))
      ),
      deps: [ConfigService, LICENSING_SERVICE],
    },
    /**
     * License-controlled features
     */
    {
      provide: DYNAMIC_FEATURE_FLAGS,
      useFactory: (config: ConfigService, licensing: ILicensingService<LicensedFeature>): Observable<LicensedFeatureFlags> => (
        config.isLocal
          ? of({ inspection: true, analysis: true })
          : licensing.validLicenses$.pipe(map(validLicenses => ({
            inspection: validLicenses.includes('inspection'),
            analysis: validLicenses.includes('analysis'),
          })))
      ),
      deps: [ConfigService, LICENSING_SERVICE],
    },
  ]);
}
